import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DrivingExperience as DrivingExperienceType } from 'lib/AutolistAPI';

import { colors } from 'components/styles/variables';
import ReviewerInfo from '../Reviews/ReviewerInfo';
import { ExperienceTitleContent, ExperienceBodyContent } from './ExperienceTextContent';

export interface DrivingExperienceProps {
  review: DrivingExperienceType;
}

export default function DrivingExperience({ review }: DrivingExperienceProps): JSX.Element {
  const { t } = useTranslation();
  const [drivingViewMore, setDrivingViewMore] = useState(false);
  const [buyingViewMore, setBuyingViewMore] = useState(false);
  const [drivingMemoryViewMore, setDrivingMemoryViewMore] = useState(false);
  const [favoriteRoadViewMore, setFavoriteRoadViewMore] = useState(false);

  return (
    <div className="owner-review">
      <ReviewerInfo reviewerName={review.author} bestLocationDisplayName={review.city} />

      {review.drivingTip?.length && (
        <>
          <ExperienceTitleContent className="author-title">
            {`${review.city} ${t('seoSrp.drivingTipsAndExperiences.drivingTips')}`}
          </ExperienceTitleContent>
          <ExperienceBodyContent expanded={drivingViewMore}>
            {review.drivingTip}
          </ExperienceBodyContent>

          {!drivingViewMore && (
            <button
              onClick={(): void => setDrivingViewMore(true)}
              className="experience-toggler-btn"
              data-testid="driving-view-more-btn"
            >
              {t('seoSrp.drivingTipsAndExperiences.readMore')}
            </button>
          )}
          {drivingViewMore && (
            <button
              onClick={(): void => setDrivingViewMore(false)}
              className="experience-toggler-btn"
              data-testid="driving-view-less-btn"
            >
              {t('seoSrp.drivingTipsAndExperiences.readLess')}
            </button>
          )}
        </>
      )}
      {review.buyingTip?.length && (
        <>
          <ExperienceTitleContent className="author-title">
            {`${review.city} ${t('seoSrp.drivingTipsAndExperiences.buyingTips')}`}
          </ExperienceTitleContent>
          <ExperienceBodyContent expanded={buyingViewMore}>
            {review.buyingTip}
          </ExperienceBodyContent>

          {!buyingViewMore && (
            <button
              onClick={(): void => setBuyingViewMore(true)}
              className="experience-toggler-btn"
              data-testid="buying-view-more-btn"
            >
              {t('seoSrp.drivingTipsAndExperiences.readMore')}
            </button>
          )}
          {buyingViewMore && (
            <button
              onClick={(): void => setBuyingViewMore(false)}
              className="experience-toggler-btn"
              data-testid="buying-view-less-btn"
            >
              {t('seoSrp.drivingTipsAndExperiences.readLess')}
            </button>
          )}
        </>
      )}
      {review.favoriteMemory?.length && (
        <>
          <ExperienceTitleContent className="author-title">
            {t('seoSrp.drivingTipsAndExperiences.favoriteMemory')}
          </ExperienceTitleContent>
          <ExperienceBodyContent expanded={drivingMemoryViewMore}>
            {review.favoriteMemory}
          </ExperienceBodyContent>

          {!drivingMemoryViewMore && (
            <button
              onClick={(): void => setDrivingMemoryViewMore(true)}
              className="experience-toggler-btn"
              data-testid="driving-memory-view-more-btn"
            >
              {t('seoSrp.drivingTipsAndExperiences.readMore')}
            </button>
          )}
          {drivingMemoryViewMore && (
            <button
              onClick={(): void => setDrivingMemoryViewMore(false)}
              className="experience-toggler-btn"
              data-testid="driving-memory-view-less-btn"
            >
              {t('seoSrp.drivingTipsAndExperiences.readLess')}
            </button>
          )}
        </>
      )}
      {review.favoriteRoad?.length && (
        <>
          <ExperienceTitleContent className="author-title">
            {t('seoSrp.drivingTipsAndExperiences.favoriteRoad')}
          </ExperienceTitleContent>
          <ExperienceBodyContent expanded={favoriteRoadViewMore}>
            {`${review.favoriteRoad}. ${review.whyRoad}`}
          </ExperienceBodyContent>

          {!favoriteRoadViewMore && (
            <button
              onClick={(): void => setFavoriteRoadViewMore(true)}
              className="experience-toggler-btn"
              data-testid="favorite-road-view-more-btn"
            >
              {t('seoSrp.drivingTipsAndExperiences.readMore')}
            </button>
          )}
          {favoriteRoadViewMore && (
            <button
              onClick={(): void => setFavoriteRoadViewMore(false)}
              className="experience-toggler-btn"
              data-testid="favorite-road-view-less-btn"
            >
              {t('seoSrp.drivingTipsAndExperiences.readLess')}
            </button>
          )}
        </>
      )}
      <style jsx>{`
        .owner-review {
          box-shadow: 0px 1px 0px #d3d3d3;
          margin: 0 16px 29px 16px;
        }

        .experience-toggler-btn {
          color: ${colors.ALR1};
          background: none;
          border: none;
          padding: 0;
          margin-bottom: 24px;
        }

        .experience-toggler-btn:hover {
          text-decoration: underline;
        }

        @media (min-width: 850px) {
          .owner-review {
            margin: 0 24px 29px 0;
          }
        }
      `}</style>

      <style jsx global>{`
        .experience-title-content.author-title {
          padding-top: 12px;
        }

        .experience-title-content.section-title {
          padding-bottom: 8px;
          padding-top: 8px;
        }
      `}</style>
    </div>
  );
}
